<script lang="ts">
  import { api } from '$shared/api';
  import { gameLabel, expiresIn } from '$lib/utils/challenge';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  let { token }: { token: string } = $props();

  interface ChallengeView {
    public_token: string;
    game_key: string;
    status: 'pending' | 'completed' | 'expired';
    challenger_id: number;
    challenger_username: string | null;
    challenger_avatar_url: string | null;
    challenger_played_at: string;
    expires_at: string;
    is_viewer_challenger: boolean;
    is_viewer_opponent: boolean;
    round_seed: Record<string, unknown>;
    challenger_score?: number;
    challenger_answers?: Record<string, unknown>;
    opponent_id?: number | null;
    opponent_username?: string | null;
    opponent_avatar_url?: string | null;
    opponent_score?: number | null;
    opponent_answers?: Record<string, unknown> | null;
    opponent_played_at?: string | null;
    winner_id?: number | null;
  }

  let challenge = $state<ChallengeView | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let copied = $state(false);

  $effect(() => {
    loading = true;
    error = null;
    api
      .get<ChallengeView>(`/challenges/${token}`)
      .then((d) => {
        challenge = d;
        loading = false;
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Challenge not found';
        error = msg;
        loading = false;
      });
  });

  const gameLabelDerived = $derived(challenge ? gameLabel(challenge.game_key) : '');

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(location.href);
      copied = true;
      setTimeout(() => {
        copied = false;
      }, 2000);
    } catch {
      console.error('Failed to copy link');
    }
  }
</script>

<div class="challenge-wrapper">
  {#if loading}
    <LoadingSkeleton lines={4} />
  {:else if error}
    <div class="error-card">
      <h1>Challenge unavailable</h1>
      <p>{error}</p>
    </div>
  {:else if challenge}
    <div class="landing-card">
      <div class="header">
        <Avatar username={challenge.challenger_username ?? '?'} avatarUrl={challenge.challenger_avatar_url} size="lg" />
        <div>
          <h1>{challenge.challenger_username ?? 'Someone'} challenged you</h1>
          <p class="subtitle">{gameLabelDerived} — head to head</p>
        </div>
      </div>

      {#if challenge.status === 'pending'}
        <div class="meta-row">
          <span class="pill pill--pending">Pending</span>
          <span class="expires">Expires in {expiresIn(challenge.expires_at)}</span>
        </div>

        {#if challenge.is_viewer_challenger}
          <p class="hint">This is your challenge. Share the link below so your opponent can accept it in the app.</p>
        {:else}
          <p class="hint">Head to the Beat Duel app to accept this challenge.</p>
        {/if}
      {:else if challenge.status === 'completed'}
        <div class="meta-row">
          <span class="pill pill--done">Completed</span>
        </div>

        <div class="result-row">
          <div class="side">
            <Avatar
              username={challenge.challenger_username ?? '?'}
              avatarUrl={challenge.challenger_avatar_url}
              size="md"
            />
            <div>
              <strong>{challenge.challenger_username ?? '?'}</strong>
              {#if challenge.winner_id === challenge.challenger_id}
                <span class="pill pill--winner">Winner</span>
              {/if}
            </div>
          </div>
          <span class="vs">vs</span>
          <div class="side">
            <Avatar username={challenge.opponent_username ?? '?'} avatarUrl={challenge.opponent_avatar_url} size="md" />
            <div>
              <strong>{challenge.opponent_username ?? '?'}</strong>
              {#if challenge.winner_id && challenge.opponent_id && challenge.winner_id === challenge.opponent_id}
                <span class="pill pill--winner">Winner</span>
              {/if}
            </div>
          </div>
        </div>
      {:else}
        <div class="meta-row">
          <span class="pill pill--expired">Expired</span>
        </div>
        <p class="hint">This challenge expired before an opponent took it.</p>
      {/if}

      <div class="info-section">
        <p class="info-text">Open in the Beat Duel app — head-to-head challenges are played there.</p>
        <p class="info-muted">
          Don't have the app yet? Beat Duel for iPhone is in beta — ask your friend for a TestFlight invite.
        </p>
      </div>
      <button type="button" class="btn btn-secondary" onclick={handleCopy}>
        {copied ? 'Copied!' : 'Copy link'}
      </button>
    </div>
  {/if}
</div>

<style>
  .challenge-wrapper {
    display: flex;
    justify-content: center;
    padding-top: 2rem;
  }

  .landing-card,
  .error-card {
    max-width: 480px;
    width: 100%;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .header {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  h1 {
    margin: 0 0 4px 0;
    font-size: 22px;
  }

  .error-card h1 {
    font-size: 20px;
    margin: 0;
  }

  .subtitle {
    margin: 0;
    color: var(--text-dim);
    font-size: 14px;
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .pill {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .pill--pending {
    background: var(--cyan-dim, rgba(6, 182, 212, 0.18));
    color: var(--cyan);
  }

  .pill--done {
    background: var(--green-dim, rgba(34, 197, 94, 0.18));
    color: var(--green);
  }

  .pill--expired {
    background: rgba(148, 163, 184, 0.2);
    color: #94a3b8;
  }

  .pill--winner {
    background: var(--pink-dim, rgba(236, 72, 153, 0.18));
    color: var(--pink);
    margin-left: 8px;
  }

  .expires {
    color: var(--text-dim);
    font-size: 13px;
  }

  .hint {
    color: var(--text-dim);
    font-size: 14px;
    margin: 0;
  }

  .result-row {
    display: flex;
    align-items: center;
    gap: 16px;
    justify-content: center;
  }

  .side {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .vs {
    color: var(--text-dim);
    font-weight: 700;
    letter-spacing: 0.08em;
  }

  .info-section {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--border);
  }

  .info-text {
    font-size: 0.9375rem;
    color: var(--text);
    margin: 0;
    line-height: 1.5;
  }

  .info-muted {
    font-size: 0.875rem;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.4;
  }

  .btn-secondary {
    background: var(--bg);
    border: 1px solid var(--border);
    color: var(--text);
    padding: 0.625rem;
    font-size: 0.9375rem;
  }

  .btn-secondary:hover {
    background: var(--surface-hover);
  }
</style>
